import React, { useEffect, useState, useCallback, useRef } from 'react';
import { kds, orders as ordersApi } from '@/lib/api';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { Clock, AlertTriangle, Printer, Volume2, VolumeX, ChefHat, Coffee, Sandwich, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { thermalPrinter } from '@/services/thermalPrinter';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// Sons de alerta - diferentes frequências
const createAlertSound = (frequency, duration) => {
  return () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);
    } catch (e) {
      console.log('Audio not supported');
    }
  };
};

// Som mais grave para 20min (alerta moderado)
const playAlert20Min = createAlertSound(440, 0.5);
// Som mais agudo e duplo para 25min (alerta urgente)
const playAlert25Min = () => {
  createAlertSound(880, 0.3)();
  setTimeout(() => createAlertSound(880, 0.3)(), 400);
};

function Timer({ createdAt, onAlert, soundEnabled }) {
  const [elapsed, setElapsed] = useState(0);
  const alerted20Ref = useRef(false);
  const alerted25Ref = useRef(false);
  
  useEffect(() => {
    const updateElapsed = () => {
      const now = new Date();
      const created = new Date(createdAt);
      const minutes = Math.floor((now - created) / 60000);
      setElapsed(minutes);
      
      if (soundEnabled) {
        if (minutes >= 20 && !alerted20Ref.current) {
          playAlert20Min();
          alerted20Ref.current = true;
          onAlert?.('20min');
        }
        
        if (minutes >= 25 && !alerted25Ref.current) {
          playAlert25Min();
          alerted25Ref.current = true;
          onAlert?.('25min');
        }
      }
    };
    
    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    
    return () => clearInterval(interval);
  }, [createdAt, soundEnabled, onAlert]);
  
  const getColorClass = () => {
    if (elapsed >= 25) return 'bg-red-600 animate-pulse';
    if (elapsed >= 20) return 'bg-orange-500';
    if (elapsed >= 15) return 'bg-orange-500';
    return 'bg-emerald-500';
  };
  
  return (
    <div className={cn("flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-sm font-bold", getColorClass())}>
      <Clock size={16} />
      <span>{elapsed} min</span>
      {elapsed >= 25 && <AlertTriangle size={16} className="animate-bounce" />}
    </div>
  );
}

export function KDSPage() {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [sectionFilter, setSectionFilter] = useState('TODOS');
  const [originFilter, setOriginFilter] = useState('TODOS');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoPrint, setAutoPrint] = useState(false);
  const prevOrderIds = useRef(new Set());
  
  // Estados para loading de ações
  const [loadingOrderId, setLoadingOrderId] = useState(null);
  
  // Estados para cancelamento
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelOrderData, setCancelOrderData] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);

  const loadOrders = useCallback(async () => {
    try {
      const params = {};
      if (originFilter !== 'TODOS') params.origin = originFilter;
      if (sectionFilter !== 'TODOS') params.production_area = sectionFilter;
      
      const response = await kds.list(params);
      const newOrders = response.data;
      
      // Verificar novos pedidos para impressão automática
      if (autoPrint) {
        const currentIds = new Set(newOrders.map(o => o.id));
        newOrders.forEach(order => {
          if (!prevOrderIds.current.has(order.id)) {
            handlePrint(order);
          }
        });
        prevOrderIds.current = currentIds;
      }
      
      setOrders(newOrders);
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
    }
  }, [originFilter, sectionFilter, autoPrint]);

  useEffect(() => {
    loadOrders();
    // Polling a cada 3 segundos para KDS responsivo
    const interval = setInterval(loadOrders, 3000);
    return () => clearInterval(interval);
  }, [loadOrders]);

  useEffect(() => {
    filterOrders();
  }, [orders, sectionFilter]);

  const filterOrders = () => {
    let filtered = [...orders];
    
    // Sempre filtrar itens do BAR (não vão para KDS)
    filtered = filtered.map(order => ({
      ...order,
      items: order.items.filter(item => {
        const area = item.production_area || 'COZINHA';
        return area !== 'BAR';
      })
    })).filter(order => order.items.length > 0);
    
    if (sectionFilter !== 'TODOS') {
      filtered = filtered.map(order => ({
        ...order,
        items: order.items.filter(item => (item.production_area || 'COZINHA') === sectionFilter)
      })).filter(order => order.items.length > 0);
    }
    
    setFilteredOrders(filtered);
  };

  const handlePrint = async (order) => {
    // Prioridade 1: Impressora termica USB (Epson TM-T88V) via Orquestrador
    if (thermalPrinter.isConnected) {
      try {
        const result = await thermalPrinter.printAllReceipts(order, {
          kitchen: true,
          full: true,
          auto: false,
          storeName: 'Hit Grill'
        });
        const orderNum = order.order_number ? `#${order.order_number}` : `#${(order.id || '').substring(0, 8).toUpperCase()}`;
        const viasMsg = result.printed ? result.printed.join(' + ') : 'cozinha + completa';
        toast.success(`Comanda ${orderNum} impressa (${viasMsg})`, { duration: 2000 });
        return;
      } catch (err) {
        console.error('Erro impressora termica:', err);
        toast.error('Falha na termica, usando impressao do navegador');
      }
    }
    
    // Fallback: window.print() se termica nao disponivel
    const printContent = `
<!DOCTYPE html>
<html>
<head>
  <style>
    @page {
      size: 80mm auto;
      margin: 0;
    }
    @media print {
      html, body {
        width: 80mm !important;
        margin: 0 !important;
        padding: 0 !important;
        height: auto !important;
        min-height: 0 !important;
        overflow: visible !important;
      }
    }
    * { box-sizing: border-box; }
    body { font-family: 'Courier New', monospace; font-size: 18px; font-weight: bold; width: 80mm; margin: 0; padding: 2mm; }
    .header { text-align: center; border-bottom: 2px dashed #000; padding-bottom: 8px; margin-bottom: 8px; }
    .order-num { font-size: 32px; font-weight: bold; letter-spacing: 1px; }
    .section { margin: 6px 0; }
    .item { margin: 6px 0; padding: 5px; background: #f0f0f0; border: 1px solid #000; }
    .item-name { font-weight: bold; font-size: 20px; }
    .item-qty { background: #000; color: #fff; padding: 2px 8px; font-weight: bold; font-size: 20px; }
    .obs { color: #000; font-weight: bold; font-style: normal; margin-top: 4px; font-size: 18px; border: 1px dashed #000; padding: 3px; }
    .footer { border-top: 2px dashed #000; padding-top: 8px; margin-top: 8px; text-align: center; font-size: 12px; }
  </style>
</head>
<body>
  <div class="header">
    <div class="order-num">${order.order_number ? `#${order.order_number}` : `#${order.id.substring(0, 8).toUpperCase()}`}</div>
    <div>${order.origin} - ${new Date(order.created_at).toLocaleTimeString('pt-BR')}</div>
    ${order.customer_name ? `<div><strong>${order.customer_name}</strong></div>` : ''}
    <div style="margin-top:4px; font-weight:bold; font-size:14px; padding:4px 8px; background:${order.payment_mode === 'ONLINE' ? (order.payment_status === 'PAGO' ? '#16a34a' : '#ca8a04') : order.cod_payment_type === 'CARTAO_ENTREGA' ? '#2563eb' : '#ea580c'}; color:white; display:inline-block; border-radius:4px;">
      ${order.payment_mode === 'ONLINE' 
        ? (order.payment_status === 'PAGO' ? 'PAGO ONLINE' : 'AGUARDANDO PAGAMENTO') 
        : order.cod_payment_type === 'DINHEIRO' 
          ? 'DINHEIRO' + (order.change_for ? ' (TROCO P/ R$' + Number(order.change_for).toFixed(2) + ')' : '')
          : order.cod_payment_type === 'CARTAO_ENTREGA' 
            ? 'CARTAO NA ENTREGA'
            : 'PAGAMENTO NA ENTREGA'}
    </div>
  </div>
  <div class="section">
    ${order.items.map(item => `
      <div class="item">
        <span class="item-qty">${item.quantity}x</span>
        <span class="item-name">${item.product_name}</span>
        <div style="font-size:11px; color:#666;">⏱ ${item.prep_time_minutes || 15} min | ${item.production_area}</div>
        ${item.modifiers && item.modifiers.length > 0 ? `
          <div style="margin-left: 20px; margin-top: 4px; font-size: 11px; color: #333; border-left: 2px solid #999; padding-left: 6px;">
            ${item.modifiers.map(mod => `<div>+ ${mod.option_name || mod.name || 'Adicional'}${mod.price_modifier ? ` (R$ ${Number(mod.price_modifier).toFixed(2)})` : ''}</div>`).join('')}
          </div>
        ` : ''}
        ${item.notes ? `<div class="obs">OBS: ${item.notes}</div>` : ''}
      </div>
    `).join('')}
  </div>
  <div class="footer">
    <div>Impresso: ${new Date().toLocaleString('pt-BR')}</div>
  </div>
</body>
</html>`;
    
    const printWindow = window.open('', '', 'width=320,height=500');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();
      printWindow.close();
    }
  };

  const handleStartPrep = async (orderId) => {
    // ============================================================
    // OPTIMISTIC UPDATE - Atualiza UI imediatamente
    // ============================================================
    setLoadingOrderId(orderId);
    setOrders(prev => prev.map(order => 
      order.id === orderId 
        ? { ...order, status: 'PREPARO', _optimistic: true }
        : order
    ));
    
    try {
      await ordersApi.updateStatus(orderId, 'PREPARO');
      toast.success('Pedido em preparo!');
      // Recarrega para garantir consistência com servidor
      loadOrders();
    } catch (error) {
      // Reverte em caso de erro
      toast.error('Erro ao atualizar');
      loadOrders();
    } finally {
      setLoadingOrderId(null);
    }
  };

  const handleFinishPrep = async (orderId) => {
    // ============================================================
    // OPTIMISTIC UPDATE - Atualiza UI imediatamente
    // ============================================================
    setLoadingOrderId(orderId);
    setOrders(prev => prev.filter(order => order.id !== orderId));
    
    try {
      // Mudar para PRONTO (Aguarda Entregador) em vez de SAIU diretamente
      await ordersApi.updateStatus(orderId, 'PRONTO');
      toast.success('Pedido pronto! Aguardando entregador.');
      // Recarrega para garantir consistência
      loadOrders();
    } catch (error) {
      toast.error('Erro ao atualizar');
      loadOrders();
    } finally {
      setLoadingOrderId(null);
    }
  };

  // Funções de cancelamento
  const openCancelModal = (order) => {
    setCancelOrderData(order);
    setCancelReason("");
    setCancelModalOpen(true);
  };

  const handleCancelOrder = async () => {
    if (!cancelOrderData || !cancelReason.trim()) {
      toast.error("Informe o motivo do cancelamento");
      return;
    }

    setCancelLoading(true);
    try {
      const response = await api.post("/api/pdv/orders/" + cancelOrderData.id + "/cancel", {
        reason: cancelReason
      });

      if (response.data.success) {
        const msg = response.data.registered_as_loss 
          ? "Pedido cancelado e registrado como perda operacional"
          : "Pedido cancelado com sucesso";
        toast.success(msg);
        setCancelModalOpen(false);
        setCancelOrderData(null);
        loadOrders();
      } else {
        toast.error(response.data.message || "Erro ao cancelar");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao cancelar pedido");
    } finally {
      setCancelLoading(false);
    }
  };

  const getSectionIcon = (section) => {
    switch (section) {
      case 'COZINHA': return <ChefHat size={20} />;
      case 'BAR': return <Coffee size={20} />;
      case 'LANCHE': return <Sandwich size={20} />;
      default: return <ChefHat size={20} />;
    }
  };

  const getSectionColor = (section) => {
    switch (section) {
      case 'COZINHA': return { border: 'border-orange-500', bg: 'bg-orange-500', text: 'text-orange-500' };
      case 'BAR': return { border: 'border-blue-500', bg: 'bg-blue-500', text: 'text-blue-500' };
      case 'LANCHE': return { border: 'border-orange-500', bg: 'bg-orange-500', text: 'text-orange-500' };
      case 'CHURRASQUEIRA': return { border: 'border-red-500', bg: 'bg-red-500', text: 'text-red-500' };
      case 'CHAPA': return { border: 'border-amber-500', bg: 'bg-amber-500', text: 'text-amber-500' };
      case 'FRITADEIRA': return { border: 'border-yellow-500', bg: 'bg-yellow-500', text: 'text-yellow-500' };
      case 'MONTAGEM': return { border: 'border-green-500', bg: 'bg-green-500', text: 'text-green-500' };
      default: return { border: 'border-slate-500', bg: 'bg-slate-500', text: 'text-slate-500' };
    }
  };

  const groupBySection = (ordersToGroup) => {
    // Objeto dinâmico - áreas de produção são criadas conforme aparecem nos pedidos
    // BAR é ignorado pois não vai para o KDS
    const sections = {};
    
    ordersToGroup.forEach(order => {
      const areas = new Set(order.items.map(item => item.production_area || 'COZINHA'));
      areas.forEach(area => {
        // Ignorar itens do BAR
        if (area === 'BAR') return;
        
        // Se a área não existe no objeto, criar dinamicamente
        if (!sections[area]) {
          sections[area] = [];
        }
        
        const orderForSection = {
          ...order,
          items: order.items.filter(item => {
            const itemArea = item.production_area || 'COZINHA';
            return itemArea === area && itemArea !== 'BAR';
          })
        };
        if (orderForSection.items.length > 0) {
          sections[area].push(orderForSection);
        }
      });
    });
    
    return sections;
  };

  const sections = sectionFilter === 'TODOS' ? groupBySection(filteredOrders) : { [sectionFilter]: filteredOrders };

  return (
    <div className="min-h-screen bg-slate-900 text-white p-6" data-testid="kds-page">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
              KDS - Kitchen Display System
            </h1>
            <p className="text-slate-400">Pedidos confirmados aguardando preparo</p>
          </div>
          <div className="flex gap-4 items-end">
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Seção</label>
              <Select value={sectionFilter} onValueChange={setSectionFilter}>
                <SelectTrigger className="w-40 bg-slate-800 text-white border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="TODOS">Todas</SelectItem>
                  <SelectItem value="COZINHA">🍳 Cozinha</SelectItem>
                  <SelectItem value="LANCHE">🍔 Lanche</SelectItem>
                  <SelectItem value="CHURRASQUEIRA">🔥 Churrasqueira</SelectItem>
                  <SelectItem value="CHAPA">🥩 Chapa</SelectItem>
                  <SelectItem value="FRITADEIRA">🍟 Fritadeira</SelectItem>
                  <SelectItem value="MONTAGEM">📦 Montagem</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Origem</label>
              <Select value={originFilter} onValueChange={setOriginFilter}>
                <SelectTrigger className="w-40 bg-slate-800 text-white border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="TODOS">Todas</SelectItem>
                  <SelectItem value="PDV">PDV</SelectItem>
                  <SelectItem value="DELIVERY">Delivery</SelectItem>
                  <SelectItem value="COMANDA">Comanda</SelectItem>
                  <SelectItem value="CARDAPIO_DIGITAL">Cardápio Digital</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant={soundEnabled ? "default" : "outline"}
              size="icon"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={cn(soundEnabled ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-700")}
              title={soundEnabled ? "Som ativado" : "Som desativado"}
            >
              {soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
            </Button>
            <Button
              variant={autoPrint ? "default" : "outline"}
              size="icon"
              onClick={() => setAutoPrint(!autoPrint)}
              className={cn(autoPrint ? "bg-purple-600 hover:bg-purple-700" : "bg-slate-700")}
              title={autoPrint ? "Impressão automática ativada" : "Impressão automática desativada"}
            >
              <Printer size={20} />
            </Button>
          </div>
        </div>
      </div>

      {Object.entries(sections).map(([section, sectionOrders]) => {
        if (sectionOrders.length === 0) return null;
        const colors = getSectionColor(section);

        return (
          <div key={section} className="mb-8">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3" style={{ fontFamily: 'Barlow Condensed' }}>
              <span className={cn("w-8 h-8 rounded-full flex items-center justify-center text-white", colors.bg)}>
                {getSectionIcon(section)}
              </span>
              <span className={colors.text}>{section}</span>
              <span className="text-slate-500">({sectionOrders.length})</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {sectionOrders.map((order) => {
                const maxPrepTime = Math.max(...order.items.map(item => item.prep_time_minutes || 15));
                
                return (
                  <div
                    key={`${section}-${order.id}`}
                    data-testid={`kds-order-${order.id}`}
                    className={cn(
                      "rounded-xl p-5 border-2 bg-slate-800",
                      colors.border,
                      "animate-in fade-in slide-in-from-bottom-4"
                    )}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <span className="text-xs text-white/60 block">Pedido</span>
                        <span className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                          {order.order_number ? `#${order.order_number}` : `#${order.id.substring(0, 8)}`}
                        </span>
                      </div>
                      <Timer createdAt={order.created_at} soundEnabled={soundEnabled} />
                    </div>

                    <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-700">
                      <div className="text-sm">
                        {order.customer_name && (
                          <p className="font-semibold text-white">{order.customer_name}</p>
                        )}
                        <span className={cn("px-2 py-0.5 rounded text-xs font-semibold",
                          order.origin === 'DELIVERY' ? 'bg-emerald-600' : 
                          order.origin === 'PDV' ? 'bg-blue-600' : 'bg-purple-600'
                        )}>
                          {order.origin}
                        </span>
                        {order.payment_mode && (
                          <span className={cn("px-2 py-0.5 rounded text-xs font-semibold ml-1",
                            order.payment_mode === 'ONLINE' && order.payment_status === 'PAGO' ? 'bg-green-600 text-white' :
                            order.payment_mode === 'ONLINE' ? 'bg-yellow-600 text-white' :
                            order.cod_payment_type === 'CARTAO_ENTREGA' ? 'bg-blue-600 text-white' :
                            'bg-orange-600 text-white'
                          )}>
                            {order.payment_mode === 'ONLINE' 
                              ? (order.payment_status === 'PAGO' ? 'PAGO ONLINE' : 'AGUARDANDO PG') 
                              : order.cod_payment_type === 'DINHEIRO' 
                                ? 'DINHEIRO' + (order.change_for ? ' (TROCO P/ R$' + order.change_for.toFixed(2) + ')' : '')
                                : order.cod_payment_type === 'CARTAO_ENTREGA' 
                                  ? 'CARTAO ENTREGA'
                                  : 'PG ENTREGA'}
                          </span>
                        )}
                      </div>
                      <div className="text-right text-xs text-slate-400">
                        <div>Tempo estimado</div>
                        <div className="text-lg font-bold text-emerald-400">{maxPrepTime} min</div>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
                      {order.items?.map((item, idx) => (
                        <div key={idx} className="bg-slate-700/50 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <span className="bg-[#FF6B6B] text-white font-bold px-2 py-0.5 rounded text-sm" style={{ fontFamily: 'Barlow Condensed' }}>
                              {item.quantity}x
                            </span>
                            <div className="flex-1">
                              <span className="font-semibold block">{item.product_name}</span>
                              <span className="text-xs text-emerald-400">⏱ {item.prep_time_minutes || 15} min</span>
                            </div>
                          </div>
                          {item.notes && (
                            <div className="mt-2 bg-red-500/20 border border-red-500/50 rounded p-2">
                              <p className="text-xs text-red-200">
                                <span className="font-semibold">OBS:</span> {item.notes}
                              </p>
                            </div>
                          )}
                          {item.modifiers && item.modifiers.length > 0 && (
                            <div className="mt-1 pl-3 border-l-2 border-slate-600">
                              {item.modifiers.map((mod, midx) => (
                                <div key={midx} className="text-xs text-emerald-400">+ {mod.option_name || mod.name}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handlePrint(order)}
                        className="flex-shrink-0 bg-slate-700 hover:bg-slate-600 text-white font-bold p-2.5 rounded-lg transition-colors"
                        title="Imprimir"
                      >
                        <Printer size={18} />
                      </button>
                      <button
                        onClick={() => openCancelModal(order)}
                        className="flex-shrink-0 bg-red-600/20 hover:bg-red-600/40 text-red-400 font-bold p-2.5 rounded-lg transition-colors"
                        title="Cancelar pedido"
                        data-testid={`kds-cancel-${order.id}`}
                      >
                        <XCircle size={18} />
                      </button>
                      {order.status === 'CONFIRMADO' ? (
                        <button
                          onClick={() => handleStartPrep(order.id)}
                          disabled={loadingOrderId === order.id}
                          data-testid={`kds-start-${order.id}`}
                          className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white font-bold py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
                        >
                          {loadingOrderId === order.id ? (
                            <Loader2 size={18} className="animate-spin" />
                          ) : null}
                          Iniciar Preparo
                        </button>
                      ) : (
                        <button
                          onClick={() => handleFinishPrep(order.id)}
                          disabled={loadingOrderId === order.id}
                          data-testid={`kds-finish-${order.id}`}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-bold py-2.5 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
                        >
                          {loadingOrderId === order.id ? (
                            <Loader2 size={18} className="animate-spin" />
                          ) : null}
                          ✓ Pronto
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {filteredOrders.length === 0 && (
        <div className="text-center py-20 text-slate-500">
          <div className="text-6xl mb-4">👨‍🍳</div>
          <p className="text-xl">Nenhum pedido aguardando preparo</p>
          <p className="text-sm mt-2">Os pedidos confirmados aparecerão aqui automaticamente</p>
        </div>
      )}

      {/* Modal de Cancelamento */}
      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <XCircle size={20} />
              Cancelar Pedido
            </DialogTitle>
          </DialogHeader>

          {cancelOrderData && (
            <div className="space-y-4">
              <div className="bg-slate-700 p-3 rounded-lg">
                <p className="text-sm text-slate-400">Pedido:</p>
                <p className="font-semibold">#{cancelOrderData.order_number || cancelOrderData.id?.slice(0, 8)}</p>
                <p className="text-xs text-slate-400 mt-1">Status atual: {cancelOrderData.status}</p>
              </div>

              {(cancelOrderData.status === "PREPARO" || cancelOrderData.status === "PRONTO") && (
                <div className="bg-amber-900/50 border border-amber-600 p-3 rounded-lg">
                  <p className="text-sm text-amber-300 font-medium flex items-center gap-1">
                    <AlertCircle size={14} />
                    Atenção: Produção já iniciada
                  </p>
                  <p className="text-xs text-amber-400 mt-1">
                    Este pedido será registrado como perda operacional (estoque não será devolvido).
                  </p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-slate-300">Motivo do cancelamento *</label>
                <select
                  className="w-full mt-1 border border-slate-600 bg-slate-700 rounded-lg p-2 text-sm text-white"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                >
                  <option value="">Selecione o motivo...</option>
                  <option value="Cliente desistiu">Cliente desistiu</option>
                  <option value="Produto indisponível">Produto indisponível</option>
                  <option value="Erro no pedido">Erro no pedido</option>
                  <option value="Demora na entrega">Demora na entrega</option>
                  <option value="Duplicidade">Duplicidade</option>
                  <option value="Outro">Outro</option>
                </select>
              </div>

              {cancelReason === "Outro" && (
                <div>
                  <label className="text-sm font-medium text-slate-300">Especifique o motivo</label>
                  <input
                    type="text"
                    className="w-full mt-1 border border-slate-600 bg-slate-700 rounded-lg p-2 text-sm text-white"
                    placeholder="Digite o motivo..."
                    onChange={(e) => setCancelReason(e.target.value)}
                  />
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                  onClick={() => setCancelModalOpen(false)}
                  disabled={cancelLoading}
                >
                  Voltar
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleCancelOrder}
                  disabled={cancelLoading || !cancelReason}
                >
                  {cancelLoading ? (
                    <>
                      <Loader2 size={14} className="mr-1 animate-spin" />
                      Cancelando...
                    </>
                  ) : (
                    "Confirmar Cancelamento"
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
